import { collection, query, where, getDocs, doc, getDoc, addDoc, updateDoc, serverTimestamp, documentId } from 'firebase/firestore';
import { db } from '../firebase/config';
import { MOVING_TYPE_LABELS } from './customerFormService';
import { getSiteSettings } from './settingsService';
import { computeDbPrice } from './dbPricingService';

/** Firestore Timestamp / Date / 문자열을 YYYY-MM-DD 문자열로 정규화 (렌더 가능한 형태) */
const normalizeDateStr = (v) => {
  if (!v) return '';
  if (typeof v === 'string') return v;
  if (typeof v?.toDate === 'function') {
    const d = v.toDate();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }
  if (typeof v?.seconds === 'number') {
    const d = new Date(v.seconds * 1000);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }
  if (v instanceof Date) {
    return `${v.getFullYear()}-${String(v.getMonth() + 1).padStart(2, '0')}-${String(v.getDate()).padStart(2, '0')}`;
  }
  return '';
};

/** N개 ID를 30개씩 묶어 `in` 쿼리로 병렬 fetch (getDoc N번 대신 쿼리 ceil(N/30)번) */
async function fetchDocsByIds(colName, ids) {
  const uniqueIds = [...new Set(ids.filter(Boolean))];
  if (uniqueIds.length === 0) return new Map();
  const chunks = [];
  for (let i = 0; i < uniqueIds.length; i += 30) {
    chunks.push(uniqueIds.slice(i, i + 30));
  }
  const snaps = await Promise.all(
    chunks.map((chunk) =>
      getDocs(query(collection(db, colName), where(documentId(), 'in', chunk))).catch(() => ({ docs: [] }))
    )
  );
  const map = new Map();
  snaps.forEach((s) => s.docs.forEach((d) => map.set(d.id, { id: d.id, ...d.data() })));
  return map;
}

/**
 * 파트너의 내 고객 리스트 (배정+구매 DB)
 */
export const getMyCustomers = async (partnerId, partnerCategories = null) => {
  if (!partnerId) return [];
  const q = query(
    collection(db, 'partner_assignments'),
    where('partner_id', '==', partnerId),
  );
  const snap = await getDocs(q);
  const assignments = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  const catFilter = Array.isArray(partnerCategories) && partnerCategories.length > 0 ? partnerCategories : null;

  // 1) service_requests batch fetch (30개씩 in 쿼리)
  const srMap = await fetchDocsByIds('service_requests', assignments.map((a) => a.service_request_id));

  // 2) customer_ids 모으고 batch fetch
  const custIds = Array.from(srMap.values()).map((sr) => sr.customer_id);
  const custMap = await fetchDocsByIds('customers', custIds);

  // 3) 가격표 1회 로드 (purchase_price 누락 데이터의 즉석 산출 fallback)
  const settings = await getSiteSettings().catch(() => null);

  const result = [];
  for (const a of assignments) {
    const serviceRequest = a.service_request_id ? srMap.get(a.service_request_id) : null;
    const customer = serviceRequest?.customer_id ? custMap.get(serviceRequest.customer_id) : null;

    const created = a.created_at?.seconds ? new Date(a.created_at.seconds * 1000) : null;
    const dateStr = created
      ? `${created.getFullYear()}.${created.getMonth() + 1}.${created.getDate()}`
      : '';

    const rawStatus = a.status || 'pre_consult';
    const status = rawStatus === 'unread' || rawStatus === 'read' ? 'pre_consult' : rawStatus;

    // 신형 ApplyFormChat은 service_requests에 name/phone/moving_address 직접 저장
    // 구형은 customers 컬렉션에 별도 저장
    const customerName = customer?.name || serviceRequest?.name || '-';
    const customerPhone = customer?.phone || serviceRequest?.phone || '';
    const movingAddress = customer?.moving_address || customer?.current_address || customer?.address
      || serviceRequest?.moving_address || serviceRequest?.movingAddress || '';
    // 희망일 — 카테고리별 날짜(이사/청소/인테리어/기타) 폴백. 청소 등은 moving_date 가 없으므로 cleaning_date 등을 본다.
    const movingDate = normalizeDateStr(
      customer?.moving_date || serviceRequest?.moving_date
      || customer?.cleaning_date || serviceRequest?.cleaning_date
      || customer?.interior_date || serviceRequest?.interior_date
      || customer?.preferred_date || serviceRequest?.preferred_date,
    );

    const srCategories = Array.isArray(serviceRequest?.services) ? serviceRequest.services
      : (serviceRequest?.category ? [serviceRequest.category] : []);

    // 업종 필터: 제휴업체 업종과 교집합 없으면 스킵
    if (catFilter && srCategories.length > 0 && !srCategories.some((c) => catFilter.includes(c))) continue;

    const visibleCategory = catFilter
      ? (srCategories.find((c) => catFilter.includes(c)) || srCategories[0] || '')
      : (srCategories[0] || '');

    // 열람가(제휴업체 결제액) 우선순위:
    //  1) partner_assignments.purchase_price (신규 분리 필드)
    //  2) computeDbPrice 즉석 산출 (가격표 기반 — 진실의 source)
    // service_requests.price 는 신뢰하지 않음 (랜딩/시드 데이터에 비정상 값이 섞여 있음)
    let purchasePrice = Number(a.purchase_price) || 0;
    if (purchasePrice <= 0 && serviceRequest && settings) {
      purchasePrice = computeDbPrice(
        settings,
        { ...serviceRequest, category: visibleCategory },
        customer,
      );
    }

    result.push({
      id: a.id,
      assignmentId: a.id,
      serviceRequestId: a.service_request_id,
      status,
      category: visibleCategory,
      createdAtMs: created ? created.getTime() : 0,
      region: movingAddress.split(' ').slice(0, 2).join(' ') || '-',
      address: movingAddress,
      movingAddress,
      movingDate,
      date: dateStr,
      name: customerName,
      phone: customerPhone,
      source: a.source || 'assigned',
      sourceLabel: a.source === 'purchased' ? 'DB 마켓' : '배정 DB',
      reservationDate: normalizeDateStr(a.reserved_date),
      price: a.price || 0,
      purchasePrice,
      areaSize: serviceRequest?.area_size || customer?.area_size || '',
      areaPyeongExact: Number(serviceRequest?.area_pyeong_exact || customer?.area_pyeong_exact || 0) || null,
      movingType: serviceRequest?.moving_type || customer?.moving_type || '',
      cleaningType: serviceRequest?.cleaning_type || customer?.cleaning_type || '',
      internetType: serviceRequest?.internet_type || customer?.internet_type || '',
      interiorStyle: serviceRequest?.interior_style || customer?.interior_style || '',
      memo: a.memo || serviceRequest?.memo || '',
      memoHistory: Array.isArray(a.memo_history) ? a.memo_history : [],
      isNew: !a.read_at && status === 'pre_consult',
    });
  }

  // 최신순 (created_at desc) — created_at 없는 건은 id로 fallback 정렬
  return result.sort((a, b) => {
    if (b.createdAtMs !== a.createdAtMs) return b.createdAtMs - a.createdAtMs;
    return b.id > a.id ? 1 : -1;
  });
};

/**
 * DB 마켓 - 미배정 서비스 요청 목록
 * @param {Array<string>} partnerCategories — 제휴업체 service_categories (있으면 해당 업종만 반환)
 */
export const getMarketDBs = async (partnerCategories = null) => {
  const q = query(collection(db, 'service_requests'), where('assigned_partner_id', '==', null));
  const snap = await getDocs(q);
  const catFilter = Array.isArray(partnerCategories) && partnerCategories.length > 0 ? partnerCategories : null;

  // 1) 카테고리 필터 먼저 적용해서 대상 service_requests 추린 뒤
  const srs = [];
  for (const d of snap.docs) {
    const sr = { id: d.id, ...d.data() };
    // 취소(고객 취소) · 미매칭 취소(이사일 경과) 건은 마컷에서 노출 제외
    if (sr.hq_status === 'cancelled' || sr.hqStatus === 'cancelled') continue;
    if (sr.hq_status === 'unmatched_cancelled' || sr.hqStatus === 'unmatched_cancelled') continue;
    const srCategories = Array.isArray(sr.services) ? sr.services
      : (sr.category ? [sr.category] : []);
    if (catFilter && !srCategories.some((c) => catFilter.includes(c))) continue;
    srs.push({ sr, srCategories });
  }

  // 2) 관련 customer_id batch fetch (30개씩 in 쿼리)
  const custMap = await fetchDocsByIds('customers', srs.map(({ sr }) => sr.customer_id));

  // 3) 가격표 1회 로드 (sr.price 누락된 구 데이터 fallback 산출용)
  const settings = await getSiteSettings().catch(() => null);

  const list = [];
  for (const { sr, srCategories } of srs) {
    const customer = sr.customer_id ? custMap.get(sr.customer_id) : null;

    const created = sr.created_at?.seconds ? new Date(sr.created_at.seconds * 1000) :
      (sr.createdAt?.seconds ? new Date(sr.createdAt.seconds * 1000) : null);
    const regDate = created
      ? `${created.getFullYear()}.${created.getMonth() + 1}.${created.getDate()}`
      : '';

    // 긴급 DB: 이사예정일이 오늘로부터 15일 이내면 긴급 표시
    let isUrgent = sr.urgent === true;
    const movingDateStr = customer?.moving_date || sr.moving_date
      || customer?.cleaning_date || sr.cleaning_date
      || customer?.interior_date || sr.interior_date
      || customer?.preferred_date || sr.preferred_date || '';
    if (movingDateStr) {
      const mv = new Date(movingDateStr);
      if (!isNaN(mv.getTime())) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const diffDays = Math.floor((mv - today) / (1000 * 60 * 60 * 24));
        if (diffDays >= 0 && diffDays <= 15) isUrgent = true;
      }
    }

    // 제휴업체 업종과 매칭되는 카테고리만 선정 (다중 업종 신청건의 경우 각 파트너에게는 자기 업종만 보이게)
    const visibleCategory = catFilter
      ? (srCategories.find((c) => catFilter.includes(c)) || srCategories[0])
      : srCategories[0];

    // 가격: 항상 가격표 기반 산출 (sr.price 는 비정상 값 섞여 있어 신뢰 X)
    // visibleCategory 기준으로 산출 (다중 카테고리 신청일 때 해당 파트너에게 보이는 업종)
    const priceForView = computeDbPrice(
      settings,
      { ...sr, category: visibleCategory },
      customer,
    );

    list.push({
      id: sr.id,
      category: visibleCategory || '',
      services: srCategories,
      urgent: isUrgent,
      region: (customer?.moving_address || customer?.current_address || customer?.address
        || sr.moving_address || '').split(' ').slice(0, 2).join(' ') || '-',
      areaSize: sr.area_size || customer?.area_size || (sr.area_pyeong_exact ? `${sr.area_pyeong_exact}평` : ''),
      movingType: MOVING_TYPE_LABELS[sr.moving_type] || sr.moving_type || '-',
      // 인터넷 서비스 종류 (internet_only / internet_tv) — 마켓 카드 상품명 매핑용
      internetType: sr.internet_type || customer?.internet_type || '',
      // 청소 종류 (basic/deep)
      cleaningType: sr.cleaning_type || customer?.cleaning_type || '',
      // 인테리어 스타일
      interiorStyle: sr.interior_style || customer?.interior_style || '',
      movingDate: normalizeDateStr(
        customer?.moving_date || sr.moving_date
        || customer?.cleaning_date || sr.cleaning_date
        || customer?.interior_date || sr.interior_date
        || customer?.preferred_date || sr.preferred_date,
      ),
      price: priceForView,
      regDate,
      memo: sr.memo || '',
    });
  }
  return list;
};

/**
 * DB 구매 - partner_assignments 생성 + service_request 배정
 */
// 관심 키워드
export const getPartnerKeywords = async (partnerId) => {
  if (!partnerId) return [];
  const q = query(collection(db, 'partner_keywords'), where('partner_id', '==', partnerId));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

export const addPartnerKeyword = async (partnerId, category, region, extra = {}) => {
  const ref = await addDoc(collection(db, 'partner_keywords'), {
    partner_id: partnerId,
    category: category || '',
    region: region || '',
    areaSize: extra.areaSize || '',
    movingDateFrom: extra.movingDateFrom || '',
    priceFrom: extra.priceFrom || '',
    priceTo: extra.priceTo || '',
    created_at: serverTimestamp(),
  });
  return ref.id;
};

export const removePartnerKeyword = async (keywordId) => {
  const { deleteDoc } = await import('firebase/firestore');
  await deleteDoc(doc(db, 'partner_keywords', keywordId));
};

export const purchaseDB = async (serviceRequestId, partnerId, { price = 10000, method = '카드' } = {}) => {
  await updateDoc(doc(db, 'service_requests', serviceRequestId), {
    assigned_partner_id: partnerId,
    hq_status: 'assigned',
    updated_at: serverTimestamp(),
  });
  await addDoc(collection(db, 'partner_assignments'), {
    service_request_id: serviceRequestId,
    partner_id: partnerId,
    status: 'pre_consult',
    source: 'purchased',
    purchase_price: price,
    price,
    created_at: serverTimestamp(),
    updated_at: serverTimestamp(),
  });
  // 결제 내역 자동 기록 (DB 구매건)
  await addDoc(collection(db, 'partner_payment_requests'), {
    partner_id: partnerId,
    service_request_id: serviceRequestId,
    amount: price,
    method,
    status: 'completed',
    label: 'DB 마켓 구매',
    created_at: serverTimestamp(),
  });
};

/**
 * 파트너 대시보드 집계
 * @param {string} partnerId - users 문서 uid (partners doc id와 동일)
 */
export const getPartnerDashboardStats = async (partnerId) => {
  const result = {
    inProgressCount: 0,
    monthlyCompletedCount: 0,
    receivableTotal: 0,
    receivableCount: 0,
    receivableList: [],
    conversionRate: 0,
    assignedConvRate: 0,
    purchasedConvRate: 0,
    pipelineCounts: {
      pre_consult: 0,
      in_progress: 0,
      consulting: 0,
      visiting: 0,
      reserved: 0,
      absent: 0,
      completed: 0,
      cancelled: 0,
      pending: 0,
    },
  };

  if (!partnerId) return result;

  // 1. partner_assignments 조회 (내 배정 전체)
  try {
    const q = query(
      collection(db, 'partner_assignments'),
      where('partner_id', '==', partnerId),
    );
    const snap = await getDocs(q);
    const assignments = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).getTime();

    let completedThisMonth = 0;

    for (const a of assignments) {
      const rawStatus = a.status || 'pre_consult';
      const status = rawStatus === 'unread' || rawStatus === 'read' ? 'pre_consult' : rawStatus;
      if (result.pipelineCounts[status] !== undefined) {
        result.pipelineCounts[status] += 1;
      }
      if (status !== 'completed' && status !== 'cancelled') {
        result.inProgressCount += 1;
      }
      if (status === 'completed') {
        const t = a.updated_at?.seconds ? a.updated_at.seconds * 1000 : 0;
        if (t >= monthStart) completedThisMonth += 1;
      }
    }

    result.monthlyCompletedCount = completedThisMonth;

    const total = assignments.length || 0;
    const completedAll = assignments.filter((a) => a.status === 'completed' || a.status === 'reserved').length;
    result.conversionRate = total ? (completedAll / total) * 100 : 0;

    // 배정 DB (source !== 'purchased')
    const assigned = assignments.filter((a) => a.source !== 'purchased');
    const assignedComplete = assigned.filter((a) => a.status === 'completed' || a.status === 'reserved').length;
    result.assignedConvRate = assigned.length ? Math.round((assignedComplete / assigned.length) * 100) : 0;

    // 구매 DB (source === 'purchased')
    const purchased = assignments.filter((a) => a.source === 'purchased');
    const purchasedComplete = purchased.filter((a) => a.status === 'completed' || a.status === 'reserved').length;
    result.purchasedConvRate = purchased.length ? Math.round((purchasedComplete / purchased.length) * 100) : 0;
  } catch { /* skip */ }

  // 2. partner_receivables 미수금
  try {
    const q = query(
      collection(db, 'partner_receivables'),
      where('partner_id', '==', partnerId),
      where('status', '==', 'pending'),
    );
    const snap = await getDocs(q);
    const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    result.receivableList = list;
    result.receivableCount = list.length;
    result.receivableTotal = list.reduce((sum, r) => sum + (r.amount || 0), 0);
  } catch { /* skip */ }

  return result;
};

// ────────── 24h 초과 미수금 검사 ──────────
// partner_receivables / partner_payment_requests 둘 다 검사
// 둘 중 하나라도 createdAt이 24시간 전이면 true
const ONE_DAY_MS = 24 * 60 * 60 * 1000;

const tsToMs = (val) => {
  if (!val) return 0;
  if (val.toMillis) return val.toMillis();
  if (val.seconds) return val.seconds * 1000;
  if (typeof val === 'number') return val;
  if (typeof val === 'string') return new Date(val).getTime();
  return 0;
};

export const hasOverdueReceivable = async (partnerId, { thresholdMs = ONE_DAY_MS } = {}) => {
  if (!partnerId) return { overdue: false, oldestMs: 0, count: 0 };
  const now = Date.now();
  const items = [];
  try {
    const recvSnap = await getDocs(query(
      collection(db, 'partner_receivables'),
      where('partner_id', '==', partnerId),
      where('status', '==', 'pending'),
    ));
    recvSnap.docs.forEach((d) => items.push(tsToMs(d.data().createdAt || d.data().created_at)));
  } catch { /* skip */ }
  try {
    const payReqSnap = await getDocs(query(
      collection(db, 'partner_payment_requests'),
      where('partner_id', '==', partnerId),
      where('status', '==', 'pending'),
    ));
    payReqSnap.docs.forEach((d) => items.push(tsToMs(d.data().createdAt || d.data().created_at)));
  } catch { /* skip */ }
  const overdueItems = items.filter((ms) => ms > 0 && now - ms > thresholdMs);
  const oldestMs = overdueItems.length ? Math.min(...overdueItems) : 0;
  return { overdue: overdueItems.length > 0, oldestMs, count: overdueItems.length };
};

// 24h 초과 미수금 보유 파트너 목록 (관리자 — 이상 파트너 관리용)
export const listOverduePartners = async ({ thresholdMs = ONE_DAY_MS } = {}) => {
  const now = Date.now();
  const partnerMap = new Map(); // partnerId -> { count, oldestMs, totalAmount }

  const aggregate = (partnerId, ms, amount) => {
    if (!partnerId || !ms || now - ms <= thresholdMs) return;
    const cur = partnerMap.get(partnerId) || { count: 0, oldestMs: ms, totalAmount: 0 };
    cur.count += 1;
    cur.totalAmount += Number(amount) || 0;
    if (ms < cur.oldestMs) cur.oldestMs = ms;
    partnerMap.set(partnerId, cur);
  };

  try {
    const recvSnap = await getDocs(query(
      collection(db, 'partner_receivables'),
      where('status', '==', 'pending'),
    ));
    recvSnap.docs.forEach((d) => {
      const data = d.data();
      aggregate(data.partner_id, tsToMs(data.createdAt || data.created_at), data.amount);
    });
  } catch { /* skip */ }

  try {
    const payReqSnap = await getDocs(query(
      collection(db, 'partner_payment_requests'),
      where('status', '==', 'pending'),
    ));
    payReqSnap.docs.forEach((d) => {
      const data = d.data();
      aggregate(data.partner_id, tsToMs(data.createdAt || data.created_at), data.amount);
    });
  } catch { /* skip */ }

  return Array.from(partnerMap.entries()).map(([partnerId, info]) => ({
    partnerId,
    overdueCount: info.count,
    overdueTotal: info.totalAmount,
    overdueSinceMs: info.oldestMs,
    overdueHours: Math.floor((now - info.oldestMs) / (60 * 60 * 1000)),
  }));
};
